import React, { useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ProductListContext } from './ProductListContext';
import { ListViewPlaceHolder } from './list-view';
import { getProductsFromGroups, PAGE_SIZE, returnObject } from './productListHelper';
import { ActionBarPlaceHolder } from './action-bar/ActionBarPlaceHolder';

export const ProductListLoader = ({
  children, loadMoreProducts
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const productListItems = useRef(null);
  let paginationObserver = null;
  let isScrollListnerAdded = false;
  const {
    list,
    productsDisplayed,
    productInfoLoading
  } = useContext(ProductListContext);

  const hasGroup = list?.groups?.length > 0;

  const { itemCount } = returnObject(list);
  const listProducts = useMemo(() => getProductsFromGroups(list), [list]);

  const disconnect = () => {
    if (paginationObserver && paginationObserver?.disconnect) {
      paginationObserver.disconnect();
    }
    if (isScrollListnerAdded) {
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('scroll', onScroll);
      isScrollListnerAdded = false;
    }
  };

  useEffect(() => {
    return () => { disconnect(); };
  }, []);

  const observe = (elem, cb) => {
    const observerOptions = {
      rootMargin: '0px 0px 300px 0px'
    };
    paginationObserver = new IntersectionObserver(async (_ref2, obs) => {
      let [entry] = _ref2;
      if (!entry.isIntersecting) return;
      disconnect();
      cb();
    }, observerOptions);
    paginationObserver.observe(elem);
  };

  const loadMore = () => {
    if (productsDisplayed?.length > 0 && productsDisplayed?.length < listProducts.length) {
      loadMoreProducts();
    }
  };

  const onScroll = (event) => {
    // this is for checking if the user reaches the bottom of the view port.
    // this act as a fail safe, if the user is scrolling too fast and the observing element
    // for intersection is already in the view port
    let documentHeight = document.body.scrollHeight;
    let currentScroll = window.scrollY + window.innerHeight;
    let modifier = isMobile ? 150 : 400;
    if (currentScroll + modifier > documentHeight) {
      const observerTrack = paginationObserver?.takeRecords ? paginationObserver.takeRecords() : [];
      if (observerTrack?.length === 0) {
        disconnect();
        loadMore();
      }
    }
  };

  useEffect(() => {
    if (productsDisplayed?.length > 0 && productsDisplayed?.length < listProducts?.length) {
      const sortablePods = productListItems.current?.querySelectorAll('.list-product');
      const totalItems = sortablePods?.length;
      if (totalItems > 0) {
        const lastChild = sortablePods[totalItems - 1];
        disconnect();
        observe(lastChild, loadMore);
      }
      if (!isScrollListnerAdded) {
        isScrollListnerAdded = true;
        document.addEventListener('scroll', onScroll);
      }
    }
  }, [productsDisplayed?.length]);

  const itemsToLoad = itemCount - productsDisplayed?.length;

  // Check if there are any intent items (items with intentId but no itemId)
  const hasIntents = useMemo(() => {
    if (!list?.groups) return false;
    return list.groups.some((group) => group.items?.some((item) => item.intentId));
  }, [list?.groups]);

  const wrapperStyles = classNames(
    'sui-flex sui-flex-wrap',
    {
      'sui-px-2': isMobile,
      'sui-px-4': !isMobile
    }
  );

  return (
    <>
      {
        !!itemsToLoad && productsDisplayed.length === 0 && !hasIntents && (
          <ActionBarPlaceHolder />
        )
      }
      <div ref={productListItems} className={wrapperStyles}>
        {children}
        {/* Show skeleton loaders for next batch when loading more products */}
        {productInfoLoading && productsDisplayed.length === 0 && itemsToLoad > 0 && (
          <div className="sui-w-full">
            <ListViewPlaceHolder
              count={Math.min(PAGE_SIZE, itemsToLoad)}
              isMobile={isMobile}
            />
          </div>
        )}
      </div>

    </>
  );
};

ProductListLoader.propTypes = {
  children: PropTypes.node.isRequired,
  loadMoreProducts: PropTypes.func.isRequired
};
